import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Icon,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";
import { getModifiedFields, reemplazarUrl } from "helpers/functions";

import {
  useCreateAsignaturasPorProfesorMutation,
  useCreateEstudiantesPorGrupoMutation,
  useEditAsignaturasPorProfesorMutation,
  useEditEstudiantesPorGrupoMutation,
  useGetAsignaturasPorProfesorQuery,
  useGetCarrerasPorGrupoByIdQuery,
  useLazyGetAsignaturasPorProfesorByIdQuery,
  useLazyGetEstudiantesPorGrupoByIdQuery,
  useGetProfesoresQuery,
} from "services";

import AsignaturaProfesorForm from "./AsignaturaProfesorForm";
import EstudiantesForm from "./EstudiantesForm";

const NewDistributionToGroup = ({ open, handleClose, id, idAsig, idEst }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();

  const [opcionesAsigAll, setOpcionesAsigAll] = useState([]);
  const [oldValuesAsig, setOldValuesAsig] = useState({});
  const [oldValuesEst, setOldValuesEst] = useState({});
  const [profesorAsignaturaStash, setProfesorAsignaturaStash] = useState([]);

  // Queries
  const { data: grupoById, isLoading: isLoadingGrupo } =
    useGetCarrerasPorGrupoByIdQuery(id, {
      skip: !id,
    });

  useEffect(() => {
    if (grupoById && !idAsig) {
      setOldValuesAsig((prev) => ({
        ...prev,
        distribucion_grupo: grupoById.url,
      }));
    }
  }, [grupoById, idAsig]);

  const { data: asig_prof, isLoading: isLoadingAsigProf } =
    useGetAsignaturasPorProfesorQuery(
      {
        filter_params: { distribucion_grupo: id, query: "{asignatura{id}}" },
        shape: { id: "asignatura.id" },
      },
      { refetchOnReconnect: true, skip: !id || !!idEst },
    );

  const { data: profesores, isLoading: isLoadingProfesores } =
    useGetProfesoresQuery(
      {
        filter_params: {
          ordering:
            "user__apellido_paterno,user__apellido_materno,user__first_name",
          query:
            "{id,url,user{first_name,apellido_paterno,apellido_materno,email},entidad{clave_del_centro}}",
        },
        shape: {
          id: "id",
          url: "url",
          nombre: "user.first_name",
          apellido_paterno: "user.apellido_paterno",
          apellido_materno: "user.apellido_materno",
        },
      },
      { refetchOnReconnect: true, skip: !!idEst },
    );

  const [getAsigXProf] = useLazyGetAsignaturasPorProfesorByIdQuery();
  const [getEstXGrupo] = useLazyGetEstudiantesPorGrupoByIdQuery();

  // Mutations
  const [createEstudiantesPorGrupo, { isLoading: isLoadingCE }] =
    useCreateEstudiantesPorGrupoMutation();
  const [createProfesorPorAsignatura, { isLoading: isLoadingPA }] =
    useCreateAsignaturasPorProfesorMutation();
  const [editProfesorPorAsignatura, { isLoading: isLoadingPAedit }] =
    useEditAsignaturasPorProfesorMutation();
  const [editEstudiantePorGrupo, { isLoading: isLoadingEGedit }] =
    useEditEstudiantesPorGrupoMutation();

  // Effects for edit mode initialization
  useEffect(() => {
    if (idAsig) {
      getAsigXProf(idAsig)
        .unwrap()
        .then((res) => {
          const asignatura = res?.asignatura?.url;
          const profesor = reemplazarUrl(res?.profesor?.url, 2);
          setOldValuesAsig({
            asignatura,
            profesor,
            distribucion_grupo: res?.distribucion_grupo?.url,
          });

          const opcionesAsigSinFiltrar =
            res.distribucion_grupo?.ciclo_interno?.asignaturas?.map((e) => ({
              nombre: e.nombre,
              url: e.url,
              key: e.clave,
            }));
          setOpcionesAsigAll(opcionesAsigSinFiltrar);
        });
    }
  }, [idAsig, getAsigXProf]);

  useEffect(() => {
    if (idEst) {
      getEstXGrupo(idEst)
        .unwrap()
        .then((res) => {
          const estudiante = res?.estudiante;
          const mappedEstudiante = [
            {
              nombre: `${estudiante?.user?.apellido_paterno} ${estudiante?.user?.apellido_materno} ${estudiante?.user?.first_name}, ${estudiante?.numero_de_control}`,
              url: estudiante?.url,
              id: estudiante?.id,
            },
          ];
          setOldValuesEst({ estudiantes: mappedEstudiante });
        });
    }
  }, [idEst, getEstXGrupo]);

  // Prepared data for forms
  const asignaturasProfSet = new Set(asig_prof?.map((e) => e.id) || []);
  const opcionesAsig = grupoById?.ciclo_interno?.asignaturas
    ?.filter((e) => !asignaturasProfSet.has(e.id))
    .map((e) => ({
      nombre: e.nombre,
      url: e.url,
      key: e.clave,
    }));

  const opcionesProf = profesores?.map((e) => ({
    nombre: `${e.apellido_paterno} ${e.apellido_materno} ${e.nombre}`,
    url: e.url,
    key: e.id,
  }));

  // Submit Handlers
  const handleSubmitAsignaturaProfesor = async (values, actions) => {
    try {
      if (idAsig) {
        const field = {
          profesor: values.profesor,
          asignatura: values.asignatura,
        };
        const modifiedFields = getModifiedFields(oldValuesAsig, field);
        if (Object.keys(modifiedFields).length !== 0) {
          await editProfesorPorAsignatura({
            id: idAsig,
            ...modifiedFields,
          }).unwrap();
          enqueueSnackbar(
            "Editada asignación de profesor y asignatura con éxito",
            { variant: "success" },
          );
        }
      } else {
        if (profesorAsignaturaStash.length > 0) {
          for (const item of profesorAsignaturaStash) {
            await createProfesorPorAsignatura({
              distribucion_grupo: item.distribucion_grupo,
              profesor: item.profesor,
              asignatura: item.asignatura,
            }).unwrap();
          }
          enqueueSnackbar("Asignadas asignaturas a profesores con éxito", {
            variant: "success",
          });
        } else if (values.profesor && values.asignatura) {
          await createProfesorPorAsignatura({
            distribucion_grupo: values.distribucion_grupo,
            profesor: values.profesor,
            asignatura: values.asignatura,
          }).unwrap();
          enqueueSnackbar("Asignada asignatura a un profesor con éxito", {
            variant: "success",
          });
        } else {
          enqueueSnackbar(
            "Debe seleccionar un profesor y asignatura, o usar la lista de agregar.",
            { variant: "warning" },
          );
          actions.setSubmitting(false);
          return;
        }
      }
      actions.setSubmitting(false);
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error al guardar Asignatura y Profesor", {
        variant: "error",
      });
      actions.setSubmitting(false);
    }
  };

  const handleSubmitEstudiantes = async (values, actions) => {
    try {
      if (idEst) {
        const field = { estudiantes: values.estudiantes };
        const modifiedFields = getModifiedFields(oldValuesEst, field);
        if (
          Object.keys(modifiedFields).length !== 0 &&
          modifiedFields.estudiantes?.length > 0
        ) {
          const onlyUrl = modifiedFields.estudiantes[0]?.url;
          await editEstudiantePorGrupo({
            id: idEst,
            estudiante: onlyUrl,
          }).unwrap();
          enqueueSnackbar("Editada asignación de estudiante con éxito", {
            variant: "success",
          });
        }
      } else {
        if (values.estudiantes.length !== 0) {
          const field = { estudiantes: values.estudiantes };
          const modifiedFields = getModifiedFields(oldValuesEst, field);
          if (Object.keys(modifiedFields).length !== 0) {
            const onlyUrl = modifiedFields.estudiantes.map((e) => ({
              estudiante: e.url,
            }));
            await createEstudiantesPorGrupo({
              distribucion_grupo: grupoById?.url,
              estudiantes: onlyUrl,
            }).unwrap();
            enqueueSnackbar("Estudiantes asignados al grupo con éxito", {
              variant: "success",
            });
          }
        }
      }
      actions.setSubmitting(false);
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error al guardar Estudiantes", { variant: "error" });
      actions.setSubmitting(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "95%",
    maxWidth: 900,
    boxShadow: 24,
    borderRadius: "12px",
    px: { xs: 2, sm: 4 },
    py: 4,
    outline: "none",
    maxHeight: "95vh",
    overflowY: "auto",
    backgroundColor: darkMode ? "#1a2035" : "#ffffff",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle}>
        {isLoadingGrupo || isLoadingAsigProf || isLoadingProfesores ? (
          <MDBox display="flex" justifyContent="center" p={5}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <>
            <MDTypography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              mb={2}
              color={darkMode ? "white" : "dark"}
            >
              Distribución de Grupo
            </MDTypography>
            {/* Header con información de la carrera */}
            <MDBox
              mb={2}
              p={2}
              sx={{
                backgroundColor: darkMode ? "#1f283e" : "#f8fafc",
                borderRadius: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              }}
            >
              <MDBox>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  textTransform="uppercase"
                  sx={{ opacity: 0.8 }}
                >
                  Carrera
                </MDTypography>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  color={darkMode ? "white" : "dark"}
                  sx={{ lineHeight: 1.2 }}
                >
                  {grupoById?.ciclo_interno?.carrera?.nombre || "-------------"}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={4}>
                <MDBox>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    textTransform="uppercase"
                    sx={{ opacity: 0.8 }}
                  >
                    Grupo
                  </MDTypography>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    color={darkMode ? "white" : "dark"}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {grupoById?.grupo?.identificador || "-------------"}
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    textTransform="uppercase"
                    sx={{ opacity: 0.8 }}
                  >
                    Ciclo
                  </MDTypography>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    color={darkMode ? "white" : "dark"}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {grupoById?.ciclo_interno?.numero_de_ciclo ||
                      "-------------"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* Acordeón Asignatura y Profesor */}
            {!idEst && (
              <Accordion
                defaultExpanded
                sx={{ borderRadius: "8px", mb: 2, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={
                    <Icon fontSize="medium" color="info">
                      expand_more
                    </Icon>
                  }
                  aria-controls="panel-asig-prof"
                  id="asig-prof-header"
                >
                  <MDTypography variant="h6" fontWeight="bold" color="dark">
                    Asignatura y Profesor
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <AsignaturaProfesorForm
                    idAsig={idAsig}
                    opcionesAsigAll={opcionesAsigAll}
                    opcionesAsig={opcionesAsig}
                    opcionesProf={opcionesProf}
                    profesorAsignaturaStash={profesorAsignaturaStash}
                    setProfesorAsignaturaStash={setProfesorAsignaturaStash}
                    handleSubmit={handleSubmitAsignaturaProfesor}
                    handleClose={handleClose}
                    isLoading={isLoadingPA || isLoadingPAedit}
                    oldValues={oldValuesAsig}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            {/* Acordeón Distribuir Estudiantes */}
            {!idAsig && (
              <Accordion
                defaultExpanded={!idEst}
                sx={{ borderRadius: "8px", boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={
                    <Icon fontSize="medium" color="info">
                      expand_more
                    </Icon>
                  }
                  aria-controls="panel-estudiantes"
                  id="estudiantes-header"
                >
                  <MDTypography variant="h6" fontWeight="bold" color="dark">
                    Distribuir estudiantes
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <EstudiantesForm
                    grupoById={grupoById}
                    idEst={idEst}
                    handleSubmit={handleSubmitEstudiantes}
                    handleClose={handleClose}
                    isLoadingSubmit={isLoadingCE || isLoadingEGedit}
                    oldValues={oldValuesEst}
                    setOldValues={setOldValuesEst}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </MDBox>
    </Modal>
  );
};

NewDistributionToGroup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string,
  idAsig: PropTypes.string,
  idEst: PropTypes.string,
};

export default NewDistributionToGroup;
